import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { PickerController } from "@ionic/angular";
import { PickerColumn, PickerColumnOption, PickerOptions } from "@ionic/core";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import deepClone from "lodash.clonedeep";
import { Select } from "@ngxs/store";
import { ConfigState } from "src/store/config/config.state";
import { firstValueFrom, Observable } from "rxjs";
import { PlatformsEnum } from "src/app/shared/enums/platforms.enum";
import { NUMPAD } from "src/app/shared/picker/picker.const";
import { PickerSymbolType } from "src/app/shared/picker/models/picker-symbol-type.enum";
import { PickerType } from "src/app/shared/picker/models/picker-type.enum";
import { ViewType } from "src/app/shared/picker/models/picker-view-type.enum";
import { PickerSymbol } from "src/app/shared/picker/models/picker-symbol.model";
import { LocalStorageService } from "src/app/services/local-storage.service";

const VALIDATION_PATTERN = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
const DATE_PLACEHOLDER = "‐";

@Component({
    selector: "picker",
    templateUrl: "./picker.component.html",
    styleUrls: ["./picker.component.scss"],
    standalone: false,
})
export class PickerComponent implements OnInit {
    @Select(ConfigState.getPlatform) platform$: Observable<string>;

    @Output() change = new EventEmitter();
    @Output() close = new EventEmitter();

    @Input() pickerType: PickerType;
    @Input() data: { [key: string]: string } = {};
    @Input() defaultType = ViewType.Picker;

    // Numpad
    @ViewChild("wrapper", { static: false }) numpad: ElementRef;
    @ViewChild("backdrop") backdrop: any;
    public value: Array<string> = [];

    public view = ViewType.Picker;
    public numpadRows: PickerSymbol[][];
    public isValid = true;
    public readonly PickerSymbolType = PickerSymbolType;
    public readonly PickerType = PickerType;
    public readonly ViewType = ViewType;
    public platformEnum = PlatformsEnum;

    // Picker
    private firstColumn: PickerColumn;
    private secondColumn: PickerColumn;
    private translations: any;
    constructor(
        private pickerCtrl: PickerController,
        private translateService: TranslateService,
        private localStorageService: LocalStorageService
    ) {}

    ngOnInit() {
        this.setTranslations();
        this.createExtraSymbolOptions();
    }

    public async setTranslations() {
        this.translations = await firstValueFrom(this.translateService.get("SHARED"));
    }

    public async show(insideSelectable: boolean = false): Promise<void> {
        const pickerStatus = this.localStorageService.readObject("PICKERVIEW");
        if (
            insideSelectable === false &&
            (pickerStatus === ViewType.Numpad ||
                (pickerStatus === null && this.defaultType === ViewType.Numpad))
        ) {
            this.switch(ViewType.Numpad);
            return;
        }

        await this.seedPicker(1, insideSelectable);

        const buttons = [];

        if (insideSelectable === false) {
            buttons.push({
                text: this.translations.NUMPAD,
                role: "select",
                handler: () => {
                    this.switch(ViewType.Numpad);
                },
            });
        }

        buttons.push({
            role: "cancel",
            text: this.translations.CANCEL,
            cssClass: insideSelectable === false ? "w-100" : "",
            handler: () => {
                this.hide();
            },
        });

        buttons.push({
            role: "ok",
            text: "OK",
            handler: ({ int, float }) => {
                if (this.pickerType === PickerType.Product) {
                    const value = `${int.value}${float?.value === undefined || float.value === 0 ? "" : `.${float.value}`}`;
                    this.triggerChange(value);
                }
                if (this.pickerType === PickerType.Date) {
                    if (this.isValid) {
                        this.triggerChange(`${int.value}:${float.value}`);
                    }
                }
            },
        });

        const options: PickerOptions = {
            mode: "ios",
            buttons: buttons,
            columns: insideSelectable ? [this.firstColumn] : [this.firstColumn, this.secondColumn],
        };

        const picker = await this.pickerCtrl.create(options);
        await picker.present();
    }

    public async hide(event?: CustomEvent) {
        if (event) {
            event.stopPropagation();
        }
        this.close.emit();
        if (this.numpad) {
            this.value = [];
            this.numpad.nativeElement.style.bottom = "-300px";
            this.backdrop.el.style.opacity = "0";
            // Sleep hax
            await new Promise((resolve) => {
                setTimeout(resolve, 300);
            });
        }

        if (this.view === ViewType.Picker) {
            this.pickerCtrl.dismiss();
            return;
        }

        this.view = null;
    }

    public removeFromValue(): void {
        this.value = this.value.slice(0, -1);
        if (this.pickerType === PickerType.Date) {
            this.isValid = this.isDateBufferValid();
        }
    }

    public addToValue(value: string): void {
        if (this.pickerType === PickerType.Date) {
            this.addDigitToDateBuffer(value);
            return;
        }
        this.value.push(value);
    }

    // Date numpad fills a fixed HH:mm mask left-to-right (like stock clock apps):
    // empty slots show a placeholder dash, the colon is rendered, never typed.
    private addDigitToDateBuffer(digit: string): void {
        if (!/^[0-9]$/.test(digit)) {
            return;
        }
        if (this.value.length >= 4) {
            return;
        }
        this.value.push(digit);
        this.isValid = this.isDateBufferValid();
    }

    public get formattedDateValue(): string {
        const slots = [0, 1, 2, 3].map((i) => this.value[i] ?? DATE_PLACEHOLDER);
        return `${slots[0]}${slots[1]}:${slots[2]}${slots[3]}`;
    }

    public get isDateBufferComplete(): boolean {
        return this.value.length === 4;
    }

    private digitsToTime(): string {
        const [h1, h2, m1, m2] = this.value;
        return `${h1}${h2}:${m1}${m2}`;
    }

    private isDateBufferValid(): boolean {
        if (!this.isDateBufferComplete) {
            return false;
        }
        return VALIDATION_PATTERN.test(this.digitsToTime());
    }

    public triggerChange(value?: string): void {
        // Picker case
        if (value) {
            this.change.emit(value);
            this.hide();
            return;
        }
        // Numpad case
        if (this.pickerType === PickerType.Date) {
            if (this.isDateBufferValid()) {
                this.change.emit(this.digitsToTime());
                this.hide();
            }
            return;
        }
        if (this.value && this.isValid) {
            this.change.emit(this.value.join(""));
            this.hide();
        }
    }

    public async switch(type: ViewType): Promise<void> {
        if (type === ViewType.Numpad) {
            this.localStorageService.writeObject("PICKERVIEW", type);
            this.view = type;
            if (this.numpad) {
                this.numpad.nativeElement.style.bottom = "0px";
            }
            if (this.backdrop) {
                this.backdrop.el.style.opacity = "0.5";
            }
            return;
        }

        if (type === ViewType.Picker) {
            this.localStorageService.writeObject("PICKERVIEW", type);
            this.numpad.nativeElement.style.bottom = "-300px";
            this.backdrop.el.style.opacity = "0";
            this.show();
        }
    }

    private async seedPicker(amount: any = "1", onlyPositive: boolean = false): Promise<void> {
        if (this.pickerType === PickerType.Date) {
            this.setHoursPickerColumns();
        }

        if (this.pickerType === PickerType.Product) {
            this.setProductPickerColumns(amount, onlyPositive);
        }
    }

    private createExtraSymbolOptions(): void {
        const numpadClone = deepClone(NUMPAD);
        const rows = numpadClone[numpadClone.length - 1];
        if (this.pickerType === PickerType.Product) {
            rows.unshift({ value: "-", type: PickerSymbolType.Symbols });
            rows.unshift({ value: ".", type: PickerSymbolType.Symbols });
        }
        this.numpadRows = numpadClone;
    }

    private setHoursPickerColumns(): void {
        const firstColumnOptions: Array<PickerColumnOption> = [];

        let defaultTime;
        if (this.data.defaultTime) {
            defaultTime = moment(this.data.defaultTime).format("HH:mm").valueOf();
        }

        const hourNow = moment().hour().toString();
        const defaultIndexes = defaultTime ? defaultTime.split(":") : null;
        let firstColumnSelectedIndex = 0;
        let text;
        for (let i = 0; i < 24; i++) {
            if (i < 10) {
                text = `0${i.toString()}`;
                firstColumnOptions.push({
                    text,
                    value: i,
                });
            } else {
                text = i.toString();
                firstColumnOptions.push({
                    text,
                    value: i,
                });
            }
            if (defaultIndexes && defaultIndexes[0] === text) {
                firstColumnSelectedIndex = i;
            }

            if (!defaultIndexes && hourNow === text) {
                firstColumnSelectedIndex = i;
            }
        }

        const columnOne: PickerColumn = {
            name: "int",
            selectedIndex: firstColumnSelectedIndex,
            options: firstColumnOptions,
        };

        let columnTwo;
        let secondColumnSelectedIndex = 0;
        if (this.data.minutesList) {
            const options = this.data.minutesList.split(",").map((minute, index) => {
                if (defaultIndexes && defaultIndexes[1] === minute) {
                    secondColumnSelectedIndex = index;
                }
                return { text: minute, value: minute };
            });
            columnTwo = {
                name: "float",
                options,
            };
        } else {
            const secondColumnOptions = [];
            for (let i = 0; i <= 5; i++) {
                secondColumnOptions.push({ text: `${i.toString()}0`, value: i * 10 });
                if (defaultIndexes && defaultIndexes[1] === `${i.toString()}0`) {
                    secondColumnSelectedIndex = i - 1;
                }
            }
            columnTwo = {
                name: "float",
                options: secondColumnOptions,
            };
        }
        columnTwo.selectedIndex = secondColumnSelectedIndex;

        this.firstColumn = columnOne;
        this.secondColumn = columnTwo;
    }

    private setProductPickerColumns(defaultAmount = "1", onlyPositive: boolean = false): void {
        const firstColumnOptions: Array<PickerColumnOption> = [];
        const amount = defaultAmount.toString().split(".");
        const iVar = onlyPositive ? 0 : -50;

        for (let i = iVar; i < 101; i++) {
            firstColumnOptions.push({
                text: i.toString(),
                value: i,
            });
        }

        let startIndex = null;

        firstColumnOptions.forEach((x, i) => {
            if (amount[0] === x.text) {
                startIndex = i;
            }
        });

        const columnOne: PickerColumn = {
            name: "int",
            selectedIndex: startIndex,
            options: firstColumnOptions,
        };

        const columnTwo = {
            name: "float",
            options: [
                { text: "0", value: 0 },
                { text: "½", value: 5 },
                { text: "⅓", value: 33 },
                { text: "⅔", value: 66 },
                { text: "¼", value: 25 },
                { text: "¾", value: 75 },
                { text: "⅕", value: 2 },
                { text: "⅖", value: 4 },
                { text: "⅗", value: 6 },
                { text: "⅘", value: 8 },
                { text: "⅙", value: 16 },
                { text: "⅚", value: 83 },
                { text: "⅐", value: 14 },
                { text: "⅛", value: 12 },
                { text: "⅜", value: 37 },
                { text: "⅝", value: 62 },
                { text: "⅞", value: 87 },
                { text: "⅜", value: 37 },
            ],
            selectedIndex: 0,
        };

        this.firstColumn = columnOne;
        this.secondColumn = columnTwo;
    }
}
